import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { SESSION_CONFIG } from '../constants/sessionConfig';

export function SessionTimeout() {
  const { signOut, isAuthenticated, claims, forceTokenRefresh, softTokenRefresh } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  
  // Get correct login route based on user type
  const getLoginRoute = useCallback(() => {
    if (claims?.accountType === 'RMS Service User') {
      // A school representative holds a password, so a timed-out SENCO has to
      // land on the password page: /rms/sign-in can only email a link, which
      // a school account never receives.
      return claims?.userType === 'RMS School Representative'
        ? '/rms/school-sign-in'
        : '/rms/sign-in';
    }
    if (claims?.accountType === 'RMS Provider User') return '/rms/provider-sign-in';
    if (claims?.accountType === 'Service User') return '/service-user/login';
    return '/login';
  }, [claims]);

  // Handle session timeout
  const handleTimeout = useCallback(() => {
    signOut();
    navigate(getLoginRoute(), { 
      state: { 
        message: 'Your session has expired. Please log in again.' 
      } 
    });
  }, [signOut, navigate, getLoginRoute]);

  // Reset activity timer when user interacts with the page
  const resetActivityTimer = useCallback(() => {
    if (!isAuthenticated || SESSION_CONFIG.TIMEOUT_STRATEGY === 'disabled') return;
    
    // Hide warning if shown
    setShowWarning(false);
    
    // Update last activity timestamp
    localStorage.setItem('lastActivity', Date.now().toString());
    
    // For activity-based timeout, also update session start if it doesn't exist
    if (SESSION_CONFIG.TIMEOUT_STRATEGY === 'activity') {
      if (!localStorage.getItem('sessionStart')) {
        localStorage.setItem('sessionStart', Date.now().toString());
      }
    }
  }, [isAuthenticated]);

  // Initialize session
  const initializeSession = useCallback(() => {
    if (!isAuthenticated || SESSION_CONFIG.TIMEOUT_STRATEGY === 'disabled') return;
    
    const now = Date.now();
    
    // Set session start if not exists (for absolute timeout)
    if (!localStorage.getItem('sessionStart')) {
      localStorage.setItem('sessionStart', now.toString());
    }
    
    // Set last activity
    localStorage.setItem('lastActivity', now.toString());
  }, [isAuthenticated]);

  // Extend session (when user chooses to continue)
  const extendSession = useCallback(() => {
    const now = Date.now();
    
    // Reset activity timer
    localStorage.setItem('lastActivity', now.toString());
    
    // For absolute timeout, reset session start as well
    if (SESSION_CONFIG.TIMEOUT_STRATEGY === 'absolute') {
      localStorage.setItem('sessionStart', now.toString());
    }
    
    setShowWarning(false);
    
    // Refresh Firebase token to ensure it's fresh
    if (SESSION_CONFIG.ENABLE_AUTO_REFRESH) {
      forceTokenRefresh().catch(error => {
        console.warn('Token refresh failed during session extension:', error);
      });
    }
  }, [forceTokenRefresh]);

  // Check session status
  const checkSession = useCallback(() => {
    if (!isAuthenticated || SESSION_CONFIG.TIMEOUT_STRATEGY === 'disabled') return;

    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0');
    const sessionStart = parseInt(localStorage.getItem('sessionStart') || '0');
    const now = Date.now();

    let shouldTimeout = false;
    let shouldWarn = false;
    let timeUntilTimeout = 0;

    if (SESSION_CONFIG.TIMEOUT_STRATEGY === 'activity') {
      // Activity-based timeout: reset timer on user activity
      const timeSinceActivity = now - lastActivity;
      timeUntilTimeout = SESSION_CONFIG.ACTIVITY_TIMEOUT_DURATION - timeSinceActivity;
      
      shouldTimeout = timeSinceActivity >= SESSION_CONFIG.ACTIVITY_TIMEOUT_DURATION;
      shouldWarn = timeSinceActivity >= (SESSION_CONFIG.ACTIVITY_TIMEOUT_DURATION - SESSION_CONFIG.WARNING_DURATION);
      
    } else if (SESSION_CONFIG.TIMEOUT_STRATEGY === 'absolute') {
      // Absolute timeout: fixed duration from session start
      const timeSinceStart = now - sessionStart;
      timeUntilTimeout = SESSION_CONFIG.ABSOLUTE_TIMEOUT_DURATION - timeSinceStart;
      
      shouldTimeout = timeSinceStart >= SESSION_CONFIG.ABSOLUTE_TIMEOUT_DURATION;
      shouldWarn = timeSinceStart >= (SESSION_CONFIG.ABSOLUTE_TIMEOUT_DURATION - SESSION_CONFIG.WARNING_DURATION);
    }

    // Handle timeout
    if (shouldTimeout) {
      console.log('Session timeout reached, signing out user');
      handleTimeout();
      return;
    }

    // Handle warning
    if (shouldWarn && SESSION_CONFIG.ENABLE_IDLE_WARNING) {
      setTimeRemaining(Math.max(0, Math.ceil(timeUntilTimeout / 1000)));
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }

    // Auto-refresh token periodically for long sessions.
    // Uses softTokenRefresh (non-forced) so the Firebase SDK returns the
    // cached token if it's still valid, only making a network call when
    // the token is expired or within 5 minutes of expiry. For Microsoft
    // SSO users this avoids unnecessary Entra roundtrips that can
    // destabilise auth state mid-session.
    if (SESSION_CONFIG.ENABLE_AUTO_REFRESH && !shouldWarn) {
      const timeSinceLastRefresh = parseInt(localStorage.getItem('lastTokenRefresh') || '0');
      if (now - timeSinceLastRefresh > 45 * 60 * 1000) {
        softTokenRefresh().then(() => {
          localStorage.setItem('lastTokenRefresh', now.toString());
        }).catch(error => {
          console.warn('Periodic token refresh failed:', error);
        });
      }
    }
  }, [isAuthenticated, handleTimeout, softTokenRefresh, claims]);

  // Set up session management
  useEffect(() => {
    if (!isAuthenticated || SESSION_CONFIG.TIMEOUT_STRATEGY === 'disabled') {
      // Clean up any existing session data if disabled or not authenticated
      if (!isAuthenticated) {
        localStorage.removeItem('lastActivity');
        localStorage.removeItem('sessionStart');
        localStorage.removeItem('lastTokenRefresh');
      }
      return;
    }

    // Initialize session
    initializeSession();

    // Set up interval to check session
    const intervalId = setInterval(checkSession, SESSION_CONFIG.PING_INTERVAL);

    // Set up event listeners for user activity (only for activity-based timeout)
    let events = [];
    if (SESSION_CONFIG.TIMEOUT_STRATEGY === 'activity') {
      events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
      events.forEach(event => document.addEventListener(event, resetActivityTimer, { passive: true }));
    }

    // Initial check
    checkSession();

    // Cleanup
    return () => {
      clearInterval(intervalId);
      events.forEach(event => document.removeEventListener(event, resetActivityTimer));
    };
  }, [isAuthenticated, initializeSession, checkSession, resetActivityTimer]);

  // Clean up session data on unmount
  useEffect(() => {
    return () => {
      if (!isAuthenticated) {
        localStorage.removeItem('lastActivity');
        localStorage.removeItem('sessionStart');
        localStorage.removeItem('lastTokenRefresh');
      }
    };
  }, [isAuthenticated]);

  // Don't render if session timeout is disabled or user not authenticated
  if (!isAuthenticated || SESSION_CONFIG.TIMEOUT_STRATEGY === 'disabled' || !showWarning) {
    return null;
  }

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ${remainingSeconds > 0 ? `and ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}` : ''}`;
    }
    return `${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          
          <h2 className="text-xl font-bold text-gray-900 mb-2">Session Timeout Warning</h2>
          
          <p className="text-gray-600 mb-4">
            {SESSION_CONFIG.TIMEOUT_STRATEGY === 'activity' 
              ? `You've been inactive for a while. Your session will expire in ${formatTime(timeRemaining)} due to inactivity.`
              : `Your session will expire in ${formatTime(timeRemaining)}.`
            }
          </p>

          <div className="space-y-3">
            <button 
              onClick={extendSession}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-medium"
            >
              Continue Session
            </button>
            
            <button 
              onClick={handleTimeout}
              className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Logout Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}